import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { UNIT_SYSTEMS } from '../utils/unitConverter';

const SystemSettingsModal = ({ show, onHide }) => {
  const [activeTab, setActiveTab] = useState('account');
  
  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-danger text-white">
            <h5 className="modal-title">
              <i className="fas fa-cogs me-2"></i>
              System Settings
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide}></button>
          </div>
          
          <div className="modal-body p-0">
            {/* Settings Navigation Tabs */}
            <ul className="nav nav-tabs nav-fill" role="tablist">
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link py-3 text-secondary ${activeTab === 'dashboard' ? 'active bg-light' : ''}`}
                  onClick={() => setActiveTab('dashboard')}
                >
                  <i className="fas fa-columns me-2"></i>
                  Dashboard Settings
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link py-3 text-secondary ${activeTab === 'units' ? 'active bg-light' : ''}`}
                  onClick={() => setActiveTab('units')}
                >
                  <i className="fas fa-ruler me-2"></i>
                  Unit Settings
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button 
                  className={`nav-link py-3 text-secondary ${activeTab === 'account' ? 'active bg-light' : ''}`}
                  onClick={() => setActiveTab('account')}
                >
                  <i className="fas fa-user-cog me-2"></i>
                  Account Settings
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content p-4">
              {activeTab === 'dashboard' && (
                <DashboardSettings onHide={onHide} />
              )}
              
              {activeTab === 'units' && (
                <UnitSettings onHide={onHide} />
              )}
              
              {activeTab === 'account' && (
                <AccountSettings />
              )}
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard Settings Component
const DashboardSettings = ({ onHide }) => {
  const [preferences, setPreferences] = useState({
    stats_cards_visible: true,
    welcome_section_visible: true,
    quick_actions_visible: true,
    role_specific_sections: {}
  });
  const [originalPreferences, setOriginalPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  // Map of synonymous role-specific keys to keep legacy and current keys in sync
  const ROLE_KEY_SYNONYMS = {
    protocol_assignments: ['recent_protocols'],
    recent_protocols: ['protocol_assignments']
  };

  const syncSynonyms = (sections = {}, key, value) => {
    const s = { ...sections };
    s[key] = value;
    (ROLE_KEY_SYNONYMS[key] || []).forEach((alt) => { s[alt] = value; });
    return s;
  };

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard/dashboard-preferences');
      console.log("Loaded preferences:", response.data);
      // Normalize any legacy/alternate role-specific keys so UI checkboxes map correctly
      const prefs = response.data || {};
      const roleSections = prefs.role_specific_sections || {};
      // If one synonym exists, propagate its value to the other synonyms
      Object.keys(ROLE_KEY_SYNONYMS).forEach((k) => {
        if (roleSections[k] !== undefined) {
          (ROLE_KEY_SYNONYMS[k] || []).forEach((alt) => {
            if (roleSections[alt] === undefined) roleSections[alt] = roleSections[k];
          });
        }
      });
      prefs.role_specific_sections = roleSections;
      setPreferences(prefs);
      setOriginalPreferences(JSON.parse(JSON.stringify(prefs))); // Deep copy for comparison
    } catch (error) {
      console.error('Error loading dashboard preferences:', error);
      // If preferences don't exist, keep defaults
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    
    // Clear any previous save status
    setSaveStatus(null);
  };

  const handleRoleSpecificToggle = (section) => {
    setPreferences(prev => {
      const roleSections = prev.role_specific_sections || {};
      // undefined means visible (true) in the UI; compute current visible state then flip it
      const currentlyVisible = roleSections[section] !== false;
      const newValue = !currentlyVisible;
      const updated = syncSynonyms(roleSections, section, newValue);
      return {
        ...prev,
        role_specific_sections: updated
      };
    });

    // Clear any previous save status
    setSaveStatus(null);
  };

  const savePreferences = async () => {
    setSaving(true);
    setSaveStatus(null);
    
    try {
      // Ensure synonyms are synchronized before sending to backend
      const roleSections = preferences.role_specific_sections || {};
      const synced = { ...roleSections };
      // If a canonical value exists for protocol_assignments or recent_protocols, propagate it
      const canonVal = (roleSections.protocol_assignments !== undefined)
        ? roleSections.protocol_assignments
        : roleSections.recent_protocols;
      if (canonVal !== undefined) {
        synced.protocol_assignments = canonVal;
        synced.recent_protocols = canonVal;
      }

      const toSave = {
        ...preferences,
        role_specific_sections: synced
      };

      // Use the correct API path
      const response = await api.put('/dashboard/dashboard-preferences', toSave);
      console.log("Save response:", response.data);

      // Update the original preferences to match the current state
      setOriginalPreferences(JSON.parse(JSON.stringify(toSave)));

      // Update preferences in localStorage for immediate UI updates
      localStorage.setItem('dashboard_preferences', JSON.stringify(toSave));

      // Show success message
      setSaveStatus({ type: 'success', message: 'Settings saved successfully!' });

      // Instead of reloading the page, just close the modal after a delay
      setTimeout(() => {
        onHide(); // Close the modal

        // Dispatch a custom event to notify other components of changes
        const event = new CustomEvent('dashboardPreferencesUpdated', { 
          detail: toSave 
        });
        window.dispatchEvent(event);
      }, 1500);
    } catch (error) {
      console.error('Error saving dashboard preferences:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all dashboard settings to defaults?')) {
      setSaving(true);
      setSaveStatus(null);
      
      try {
        // Use the correct API path
        const response = await api.post('/dashboard/dashboard-preferences/reset');
        console.log("Reset response:", response.data);
        
        // Update local state with the reset preferences
        setPreferences(response.data.preferences);
        setOriginalPreferences(JSON.parse(JSON.stringify(response.data.preferences)));
        
        // Update preferences in localStorage
        localStorage.setItem('dashboard_preferences', JSON.stringify(response.data.preferences));
        
        // Show success message
        setSaveStatus({ type: 'success', message: 'Settings reset to defaults!' });
        
        // Instead of reloading the page, just close the modal after a delay
        setTimeout(() => {
          onHide(); // Close the modal
          
          // Optionally dispatch a custom event to notify other components of changes
          const event = new CustomEvent('dashboardPreferencesUpdated', { 
            detail: response.data.preferences 
          });
          window.dispatchEvent(event);
        }, 1500);
      } catch (error) {
        console.error('Error resetting dashboard preferences:', error);
        setSaveStatus({ type: 'error', message: 'Failed to reset settings. Please try again.' });
      } finally {
        setSaving(false);
      }
    }
  };

  const getRoleSpecificOptions = () => {
    // Get user role from context or localStorage
    const userRole = localStorage.getItem('userRole') || 'program_admin';
    
    switch (userRole) {
      case 'doctor':
        return [
          { key: 'patient_questions', label: 'Patient Questions Section', description: 'Show recent patient questions on your dashboard' },
          { key: 'upcoming_appointments', label: 'Upcoming Appointments', description: 'Show your scheduled appointments' },
          { key: 'protocol_assignments', label: 'Recent Protocol Assignments', description: 'Show your recent protocol assignments' }
        ];
      case 'program_admin':
        return [
          { key: 'system_overview', label: 'System Overview', description: 'Show system-wide statistics and alerts' },
          { key: 'recent_protocols', label: 'Recent Protocols', description: 'Show recently created protocols' }
        ];
      case 'medical_admin':
        return [
          { key: 'recent_activities', label: 'Recent Activities', description: 'Show recent patient interactions' },
          { key: 'pending_approvals', label: 'Pending Approvals', description: 'Show items pending your approval' }
        ];
      default:
        return [];
    }
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    if (!originalPreferences) return false;
    
    // Compare basic settings
    if (preferences.stats_cards_visible !== originalPreferences.stats_cards_visible ||
        preferences.welcome_section_visible !== originalPreferences.welcome_section_visible ||
        preferences.quick_actions_visible !== originalPreferences.quick_actions_visible) {
      return true;
    }
    
    // Compare role_specific_sections
    const original = originalPreferences.role_specific_sections || {};
    const current = preferences.role_specific_sections || {};
    
    for (const key in current) {
      if (current[key] !== original[key]) return true;
    }
    
    for (const key in original) {
      if (current[key] !== original[key]) return true;
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading your preferences...</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <p className="text-muted">
          Customize your dashboard by choosing which sections to display.
        </p>
        
        {/* Status message */}
        {saveStatus && (
          <div className={`alert alert-${saveStatus.type === 'success' ? 'success' : 'danger'} mt-3`}>
            <i className={`fas fa-${saveStatus.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
            {saveStatus.message}
          </div>
        )}
      </div>

      {/* Main Dashboard Sections */}
      <div className="mb-4">
        <h6 className="fw-bold mb-3 text-primary">
          <i className="fas fa-th-large me-2"></i>
          Main Dashboard Sections
        </h6>
        
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="statsCards"
                    checked={preferences.stats_cards_visible}
                    onChange={() => handleToggle('stats_cards_visible')}
                  />
                  <label className="form-check-label fw-bold" htmlFor="statsCards">
                    Statistics Cards
                  </label>
                </div>
                <small className="text-muted">
                  Shows key metrics like patient count, doctor count, and other statistics
                </small>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="welcomeSection"
                    checked={preferences.welcome_section_visible}
                    onChange={() => handleToggle('welcome_section_visible')}
                  />
                  <label className="form-check-label fw-bold" htmlFor="welcomeSection">
                    Welcome Section
                  </label>
                </div>
                <small className="text-muted">
                  Shows welcome message and role-specific information
                </small>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-body">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="quickActions"
                    checked={preferences.quick_actions_visible}
                    onChange={() => handleToggle('quick_actions_visible')}
                  />
                  <label className="form-check-label fw-bold" htmlFor="quickActions">
                    Quick Actions
                  </label>
                </div>
                <small className="text-muted">
                  Shows quick action buttons for common tasks
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Role-Specific Sections */}
      {getRoleSpecificOptions().length > 0 && (
        <div className="mb-4">
          <h6 className="fw-bold mb-3 text-primary">
            <i className="fas fa-user-tag me-2"></i>
            Role-Specific Sections
          </h6>
          
          <div className="row g-3">
            {getRoleSpecificOptions().map((option) => (
              <div key={option.key} className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={option.key}
                        checked={preferences.role_specific_sections[option.key] !== false}
                        onChange={() => handleRoleSpecificToggle(option.key)}
                      />
                      <label className="form-check-label fw-bold" htmlFor={option.key}>
                        {option.label}
                      </label>
                    </div>
                    <small className="text-muted">
                      {option.description}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button 
          type="button" 
          className="btn btn-outline-danger" 
          onClick={handleReset}
          disabled={saving}
        >
          <i className="fas fa-undo me-1"></i> Reset to Defaults
        </button>
        
        <div>
                    
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={savePreferences}
            disabled={saving || !hasChanges()}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className="toast show" role="alert">
            <div className="toast-header">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              <strong className="me-auto">Saving changes</strong>
            </div>
            <div className="toast-body">
              Your preferences are being updated...
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Unit Settings Component
const UnitSettings = ({ onHide }) => {
  const [preferences, setPreferences] = useState({
    preferred_unit_system: 'international'
  });
  const [originalPreferences, setOriginalPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard/dashboard-preferences');
      console.log("Loaded unit preferences:", response.data);
      const prefs = response.data || {};
      const unitPrefs = {
        preferred_unit_system: prefs.preferred_unit_system || 'international'
      };
      setPreferences(unitPrefs);
      setOriginalPreferences(JSON.parse(JSON.stringify(unitPrefs))); // Deep copy for comparison
    } catch (error) {
      console.error('Error loading unit preferences:', error);
      // Keep defaults if loading fails
    } finally {
      setLoading(false);
    }
  };

  const handleUnitSystemChange = (unitSystem) => {
    setPreferences(prev => ({
      ...prev,
      preferred_unit_system: unitSystem
    }));
    
    // Clear any previous save status
    setSaveStatus(null);
  };

  const savePreferences = async () => {
    setSaving(true);
    setSaveStatus(null);
    
    try {
      // Load current dashboard preferences
      const currentPrefs = await api.get('/dashboard/dashboard-preferences');
      const updatedPrefs = {
        ...currentPrefs.data,
        preferred_unit_system: preferences.preferred_unit_system
      };

      // Update dashboard preferences with unit setting
      const response = await api.put('/dashboard/dashboard-preferences', updatedPrefs);
      console.log("Unit preferences saved:", response.data);

      // Update the original preferences to match the current state
      setOriginalPreferences(JSON.parse(JSON.stringify(preferences)));

      // Update preferences in localStorage for immediate UI updates
      localStorage.setItem('dashboard_preferences', JSON.stringify(updatedPrefs));

      // Show success message
      setSaveStatus({ type: 'success', message: 'Unit preferences saved successfully!' });

      // Dispatch custom event to notify other components
      const event = new CustomEvent('unitPreferencesUpdated', { 
        detail: preferences 
      });
      window.dispatchEvent(event);

      // Close modal after delay
      setTimeout(() => {
        onHide();
      }, 1500);
    } catch (error) {
      console.error('Error saving unit preferences:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save unit preferences. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    if (!originalPreferences) return false;
    return preferences.preferred_unit_system !== originalPreferences.preferred_unit_system;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading your unit preferences...</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <p className="text-muted">
          Choose your preferred measurement system for displaying medical data and case information.
        </p>
        
        {/* Status message */}
        {saveStatus && (
          <div className={`alert alert-${saveStatus.type === 'success' ? 'success' : 'danger'} mt-3`}>
            <i className={`fas fa-${saveStatus.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
            {saveStatus.message}
          </div>
        )}
      </div>

      {/* Unit System Selection */}
      <div className="mb-4">
        <h6 className="fw-bold mb-3 text-primary">
          <i className="fas fa-ruler-combined me-2"></i>
          Measurement Unit System
        </h6>
        
        <div className="row g-3">
          <div className="col-md-6">
            <div 
              className={`card h-100 ${preferences.preferred_unit_system === UNIT_SYSTEMS.INTERNATIONAL ? 'border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleUnitSystemChange(UNIT_SYSTEMS.INTERNATIONAL)}
            >
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <input
                    className="form-check-input me-3"
                    type="radio"
                    name="unitSystem"
                    id="international"
                    checked={preferences.preferred_unit_system === UNIT_SYSTEMS.INTERNATIONAL}
                    onChange={() => handleUnitSystemChange(UNIT_SYSTEMS.INTERNATIONAL)}
                  />
                  <div>
                    <h6 className="card-title mb-1 fw-bold">International System (SI)</h6>
                    <small className="text-muted">Metric Units</small>
                  </div>
                </div>
                <div className="ps-4">
                  <div className="row">
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Weight</small>
                      <span className="fw-bold">kg (Kilograms)</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Height</small>
                      <span className="fw-bold">cm (Centimeters)</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Circumference</small>
                      <span className="fw-bold">cm (Centimeters)</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Blood Glucose</small>
                      <span className="fw-bold">mmol/L</span>
                    </div>
                  </div>
                </div>
                {preferences.preferred_unit_system === UNIT_SYSTEMS.INTERNATIONAL && (
                  <div className="mt-3">
                    <span className="badge bg-primary">
                      <i className="fas fa-check me-1"></i>Selected
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div 
              className={`card h-100 ${preferences.preferred_unit_system === UNIT_SYSTEMS.IMPERIAL ? 'border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleUnitSystemChange(UNIT_SYSTEMS.IMPERIAL)}
            >
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <input
                    className="form-check-input me-3"
                    type="radio"
                    name="unitSystem"
                    id="imperial"
                    checked={preferences.preferred_unit_system === UNIT_SYSTEMS.IMPERIAL}
                    onChange={() => handleUnitSystemChange(UNIT_SYSTEMS.IMPERIAL)}
                  />
                  <div>
                    <h6 className="card-title mb-1 fw-bold">Imperial System</h6>
                    <small className="text-muted">US Customary Units</small>
                  </div>
                </div>
                <div className="ps-4">
                  <div className="row">
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Weight</small>
                      <span className="fw-bold">lb (Pounds)</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Height</small>
                      <span className="fw-bold">ft (Feet)</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Circumference</small>
                      <span className="fw-bold">in (Inches)</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Blood Glucose</small>
                      <span className="fw-bold">mg/dL</span>
                    </div>
                  </div>
                </div>
                {preferences.preferred_unit_system === UNIT_SYSTEMS.IMPERIAL && (
                  <div className="mt-3">
                    <span className="badge bg-primary">
                      <i className="fas fa-check me-1"></i>Selected
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="alert alert-info mb-4">
        <i className="fas fa-info-circle me-2"></i>
        <strong>Note:</strong> This setting affects how all medical measurements are displayed throughout the application, including patient cases, reports, and AI responses. Your measurements will be automatically converted to your preferred units.
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end mt-4">
        <button 
          type="button" 
          className="btn btn-primary" 
          onClick={savePreferences}
          disabled={saving || !hasChanges()}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save me-1"></i> Save Unit Preferences
            </>
          )}
        </button>
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className="toast show" role="alert">
            <div className="toast-header">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              <strong className="me-auto">Saving unit preferences</strong>
            </div>
            <div className="toast-body">
              Your measurement preferences are being updated...
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Account Settings Component
const AccountSettings = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNameUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      await api.put('/user/profile', { name: formData.name });
      setSuccess('Name updated successfully!');
      
      // Update name in local storage if you store user data there
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...userData, name: formData.name }));
      
    } catch (error) {
      console.error('Error updating name:', error);
      setError('Failed to update name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validate passwords
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }
    
    setSaving(true);
    
    try {
      await api.put('/users/user/password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      });
      
      setSuccess('Password updated successfully!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.response?.status === 401) {
        setError('Current password is incorrect.');
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-4">
      <div className="mb-4">
        <p className="text-muted">
          Change your account password below.
        </p>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success mb-4">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <form onSubmit={handlePasswordUpdate}>
            <div className="mb-3">
              <label htmlFor="currentPassword" className="form-label fw-bold">Current Password</label>
              <input
                type="password"
                className="form-control"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label fw-bold">New Password</label>
              <input
                type="password"
                className="form-control"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                required
                minLength={6}
              />
              <div className="form-text text-muted">
                Password must be at least 6 characters long.
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label fw-bold">Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="d-grid gap-2">
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={saving || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Updating Password...
                  </>
                ) : (
                  <>
                    <i className="fas fa-key me-1"></i> Change Password
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsModal;